import Divider from "components/BaseWidgets/divider";
import RadioButton from "components/BaseWidgets/radioButton";
import Select from "components/BaseWidgets/select";
import Tag from "components/BaseWidgets/tag";
import React from "react";
import { Form } from "antd";
import {
  formRadioFields,
  formSelectFields,
  radioFields,
  selectFields,
  tagOptions,
} from "./helper";
import GoPremiumCard from "components/CustomWidgets/goPremiumCard";
import { usePremium } from "context/PremiumContext";
function PremiumForm({ selectOptions = [], form }) {
  const { isPremium } = usePremium();
  return (
    <div className="flex flex-col w-full gap-6 premium-form">
      {["Weight"].map((label, idx) => (
        <Form.Item
          key={idx}
          label={<span className="font-title-xs">{label}</span>}
        >
          <div className="flex items-center gap-4 w-full">
            {/* From Select */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev[label.toLowerCase()]?.to !== curr[label.toLowerCase()]?.to
              }
            >
              {() => {
                const toValue = form.getFieldValue([label.toLowerCase(), "to"]);
                return (
                  <Form.Item name={[label.toLowerCase(), "from"]} noStyle>
                    <Select
                      widthVariant="wFull"
                      className="w-full"
                      placeholder="From"
                      options={(selectOptions[label.toLowerCase()] || []).map(
                        (opt) => ({
                          ...opt,
                          // disable values greater than current To
                          disabled: toValue
                            ? Number(opt.value) > Number(toValue)
                            : false,
                        }),
                      )}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <span className="font-body-lg fw-500">to</span>

            {/* To Select */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev[label.toLowerCase()]?.from !==
                curr[label.toLowerCase()]?.from
              }
            >
              {() => {
                const fromValue = form.getFieldValue([
                  label.toLowerCase(),
                  "from",
                ]);
                return (
                  <Form.Item
                    name={[label.toLowerCase(), "to"]}
                    noStyle
                    rules={[
                      {
                        validator: (_, toValue) => {
                          const fromValue = form.getFieldValue([
                            label.toLowerCase(),
                            "from",
                          ]);

                          if (
                            (fromValue && !toValue) ||
                            (!fromValue && toValue)
                          ) {
                            return Promise.reject(
                              new Error("Please select both From and To"),
                            );
                          }

                          if (
                            fromValue &&
                            toValue &&
                            Number(fromValue) > Number(toValue)
                          ) {
                            return Promise.reject(
                              new Error(
                                "Range is invalid: From cannot be greater than To",
                              ),
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      widthVariant="wFull"
                      className="w-full"
                      placeholder="To"
                      options={(selectOptions[label.toLowerCase()] || []).map(
                        (opt) => ({
                          ...opt,
                          // disable values smaller than current From
                          disabled: fromValue
                            ? Number(opt.value) < Number(fromValue)
                            : false,
                        }),
                      )}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </Form.Item>
      ))}

      <Divider />

      {formSelectFields
        .filter((field) => !["age", "height", "weight"].includes(field.key))
        .map(({ label, key, multiple }, idx) => (
          <Form.Item
            key={idx}
            name={key}
            label={<span className="font-title-xs">{label}</span>}
          >
            <Select
              widthVariant="wFull"
              options={selectOptions[key] || []}
              mode={multiple ? "multiple" : undefined}
            />
          </Form.Item>
        ))}

      <Divider />

      {formRadioFields.map(({ label, key, options }, idx) => (
        <React.Fragment key={key}>
          <Form.Item
            initialValue={
              {
                disability_filter: "No",
                drink_filter: 2,
                smoke_filter: 2,
              }[key]
            }
            name={key}
            label={<span className="font-title-xs">{label}</span>}
          >
            <RadioButton
              options={options}
              variant="secondary"
              className="ml-auto"
            />
          </Form.Item>
          {idx < formRadioFields.length - 1 && <Divider />}
        </React.Fragment>
      ))}

      {/* Select fields */}
      {selectFields.map(({ label, key, multiple }) => (
        <div className="item-container" key={key}>
          <span className="font-title-xs">{label}</span>
          <Form.Item name={key} initialValue={multiple ? [] : null} noStyle>
            <Select
              widthVariant="wFull"
              options={selectOptions[key]}
              mode={multiple ? "multiple" : undefined}
              placeholder={`Select ${label}`}
            />
          </Form.Item>
        </div>
      ))}

      <Divider />

      {/* Radio fields */}
      {radioFields.map(({ label, key, options }) => (
        <React.Fragment key={key}>
          <div className="item-container radio-container">
            <span className="font-title-xs">{label}</span>
            <Form.Item
              name={key}
              initialValue={isPremium ? options[0]?.value : undefined}
              noStyle
            >
              <RadioButton
                options={options}
                variant="secondary"
                className="ml-auto"
              />
            </Form.Item>
          </div>
          <Divider />
        </React.Fragment>
      ))}

      {/* Tag options */}
      <div className="flex flex-col gap-3">
        <span className="font-title-xs">Location sort by</span>
        <Form.Item name="location_filter" initialValue={[]} noStyle>
          <TagSelector />
        </Form.Item>
      </div>

      {!isPremium && (
        <GoPremiumCard
          title="To unlock more search filters"
          outlineBlur
          className="blur-card"
        />
      )}
    </div>
  );
}

/* Custom wrapper to handle Tag clicks as form value */
function TagSelector({ value = [], onChange, disabled }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tagOptions.map((tag) => (
        <Tag
          key={tag.value}
          isActive={value.includes(tag.value)}
          onClick={() => {
            if (disabled) return;
            const newValue = value.includes(tag.value)
              ? value.filter((v) => v !== tag.value)
              : [...value, tag.value];
            onChange?.(newValue);
          }}
        >
          {tag.label}
        </Tag>
      ))}
    </div>
  );
}

export default PremiumForm;
